import { Delete, Download, Edit, Refresh, Upload } from "@mui/icons-material";
import {
	Box,
	Button,
	CircularProgress,
	Grid2,
	IconButton,
	Paper,
	Switch,
	Tooltip,
	Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
	ColDef,
	GridReadyEvent,
	ICellRendererParams,
	RowDoubleClickedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import moment from "moment";
import React, { type ReactNode, useEffect, useMemo, useState } from "react";
import { ErrorModal } from "../../../../components/modals/error-modal";
import { CenteredLazyLoading } from "../../../../components/shared/CenteredLazyLoading";
import { ConfirmationDialogRaw } from "../../../../controls/modals/ConfirmationDialogRaw";
import { NotificationSnackBar } from "../../../../controls/snackbars/notification-snackbar";
import { themeDXT } from "../../../../css/themeMESF";
import type { AppRelease, AppReleaseApp } from "../models/AppRelease";
import { formatFileSize } from "../models/versions";
import {
	deleteAppRelease,
	downloadAppRelease,
	getAppReleases,
	setAppReleaseActive,
} from "../repositories/AppReleasesRepository";
import { EditReleaseModal } from "./EditReleaseModal";
import { UploadReleaseModal } from "./UploadReleaseModal";

const formatDateTime = (value?: Date | string | null) =>
	value ? moment(value).format("YYYY-MM-DD HH:mm") : "";

export const useAppReleases = (app: AppReleaseApp) =>
	useQuery<AppRelease[], Error>({
		queryKey: ["appReleases", app.appCode, app.platform],
		queryFn: ({ signal }) =>
			getAppReleases({ appCode: app.appCode, platform: app.platform }, signal),
	});

type ActionsCellParams = ICellRendererParams<AppRelease> & {
	onEdit: (release: AppRelease) => void;
	onDownload: (release: AppRelease) => void;
	onDelete: (release: AppRelease) => void;
	downloadingId: number | null;
};

const ActionsCellRenderer = ({
	data,
	onEdit,
	onDownload,
	onDelete,
	downloadingId,
}: ActionsCellParams) => {
	if (!data) {
		return null;
	}

	const isDownloading = downloadingId === data.AppReleaseId;

	return (
		<Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
			<Tooltip title="Edit">
				<IconButton size="small" onClick={() => onEdit(data)}>
					<Edit fontSize="small" color="primary" />
				</IconButton>
			</Tooltip>
			<Tooltip title="Download APK">
				<span>
					<IconButton
						size="small"
						disabled={isDownloading || !data.UploadFileId}
						onClick={() => onDownload(data)}
					>
						{isDownloading ? (
							<CircularProgress size={18} />
						) : (
							<Download fontSize="small" color="primary" />
						)}
					</IconButton>
				</span>
			</Tooltip>
			<Tooltip
				title={
					data.IsActive ? "The active release cannot be deleted" : "Delete"
				}
			>
				<span>
					<IconButton
						size="small"
						disabled={!!data.IsActive}
						onClick={() => onDelete(data)}
					>
						<Delete
							fontSize="small"
							color={data.IsActive ? "disabled" : "error"}
						/>
					</IconButton>
				</span>
			</Tooltip>
		</Box>
	);
};

type Props = {
	app: AppReleaseApp;
	appSelector?: ReactNode;
};

export const TableAppReleases = ({ app, appSelector }: Props) => {
	const queryClient = useQueryClient();

	const [showUpload, setShowUpload] = useState(false);
	const [releaseToEdit, setReleaseToEdit] = useState<AppRelease | null>(null);
	const [releaseToToggle, setReleaseToToggle] = useState<AppRelease | null>(
		null,
	);
	const [releaseToDelete, setReleaseToDelete] = useState<AppRelease | null>(
		null,
	);
	const [downloadingId, setDownloadingId] = useState<number | null>(null);
	const [message, setMessage] = useState("");
	const [error, setError] = useState("");

	const {
		data: releases,
		isLoading,
		isFetching,
		isError,
		error: queryError,
		refetch,
	} = useAppReleases(app);

	const activeRelease = useMemo(
		() => (releases ?? []).find((release) => release.IsActive) ?? null,
		[releases],
	);

	useEffect(() => {
		if (isError && queryError) {
			setError(queryError.message);
		}
	}, [isError, queryError]);

	const activeMutation = useMutation(setAppReleaseActive, {
		onSuccess: async (_result, { isActive }) => {
			await queryClient.invalidateQueries(["appReleases"]);
			setMessage(
				`The release was ${isActive ? "activated" : "deactivated"} successfully`,
			);
		},
		onError: (mutationError: Error) => setError(mutationError.message),
	});

	const deleteMutation = useMutation(deleteAppRelease, {
		onSuccess: async () => {
			await queryClient.invalidateQueries(["appReleases"]);
			setMessage("The release was deleted successfully");
		},
		onError: (mutationError: Error) => setError(mutationError.message),
	});

	const handleDownload = async (release: AppRelease) => {
		setDownloadingId(release.AppReleaseId);
		try {
			await downloadAppRelease(release);
		} catch (downloadError) {
			setError(
				downloadError instanceof Error
					? downloadError.message
					: "Error downloading the app release",
			);
		} finally {
			setDownloadingId(null);
		}
	};

	const onToggleConfirmationClose = (value?: string) => {
		const release = releaseToToggle;
		setReleaseToToggle(null);

		if (value === "OK" && release) {
			activeMutation.mutate({
				appReleaseId: release.AppReleaseId,
				isActive: !release.IsActive,
			});
		}
	};

	const onDeleteConfirmationClose = (value?: string) => {
		const release = releaseToDelete;
		setReleaseToDelete(null);

		if (value === "OK" && release) {
			deleteMutation.mutate(release.AppReleaseId);
		}
	};

	const defaultColDef = useMemo<ColDef>(
		() => ({
			flex: 1,
			sortable: true,
			resizable: true,
			suppressHeaderMenuButton: true,
			cellStyle: {
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
			},
		}),
		[],
	);

	const columnDefs = useMemo<ColDef<AppRelease>[]>(
		() => [
			{
				field: "Version",
				headerName: "Version",
				flex: 1,
				minWidth: 100,
				cellRenderer: ({ data, value }: ICellRendererParams<AppRelease>) =>
					data?.ReleaseNotes ? (
						<Tooltip
							title={
								<span style={{ whiteSpace: "pre-wrap" }}>
									{data.ReleaseNotes}
								</span>
							}
						>
							<span style={{ textDecoration: "underline dotted" }}>
								{value}
							</span>
						</Tooltip>
					) : (
						value
					),
			},
			{ field: "Build", headerName: "Build", flex: 0.7, minWidth: 80 },
			{
				field: "MinSupportedVersion",
				headerName: "Min supported",
				minWidth: 110,
				valueGetter: ({ data }) => data?.MinSupportedVersion || "-",
			},
			{
				field: "IsActive",
				headerName: "Active",
				flex: 0.7,
				minWidth: 80,
				cellRenderer: ({ data }: ICellRendererParams<AppRelease>) =>
					data ? (
						<Tooltip title={data.IsActive ? "Deactivate" : "Activate"}>
							<Switch
								checked={!!data.IsActive}
								size="small"
								color="primary"
								onClick={(event) => event.stopPropagation()}
								onDoubleClick={(event) => event.stopPropagation()}
								onChange={() => setReleaseToToggle(data)}
							/>
						</Tooltip>
					) : null,
			},
			{
				field: "PublishedAt",
				headerName: "Published",
				minWidth: 140,
				valueFormatter: ({ value }) => formatDateTime(value) || "-",
			},
			{
				field: "FileSize",
				headerName: "Size",
				flex: 0.7,
				minWidth: 90,
				valueFormatter: ({ value }) => formatFileSize(value),
			},
			{
				field: "Sha256",
				headerName: "SHA-256",
				minWidth: 110,
				cellRenderer: ({ value }: ICellRendererParams<AppRelease>) =>
					value ? (
						<Tooltip title={value}>
							<span style={{ fontFamily: "monospace" }}>
								{String(value).slice(0, 12)}
							</span>
						</Tooltip>
					) : null,
			},
			{
				field: "CreatedBy",
				headerName: "Created by",
				minWidth: 120,
				tooltipValueGetter: ({ data }) => formatDateTime(data?.CreatedAt),
			},
			{
				headerName: "",
				colId: "actions",
				sortable: false,
				resizable: false,
				minWidth: 130,
				flex: 0.9,
				cellRenderer: ActionsCellRenderer,
				cellRendererParams: {
					onEdit: setReleaseToEdit,
					onDownload: handleDownload,
					onDelete: setReleaseToDelete,
					downloadingId,
				},
			},
		],
		// handleDownload only uses state setters, so it is safe to omit.
		[downloadingId],
	);

	return (
		<>
			<Grid2 container justifyContent="center" p={1} spacing={1}>
				<Grid2
					size={{ xs: 12 }}
					display="flex"
					alignItems="center"
					flexWrap="wrap"
					gap={2}
				>
					<Typography variant="h5" fontWeight={600} flexGrow={1}>
						App Releases
					</Typography>
					{appSelector}
					<Tooltip title="Refresh">
						<span>
							<IconButton onClick={() => refetch()} disabled={isFetching}>
								<Refresh />
							</IconButton>
						</span>
					</Tooltip>
					<Button
						variant="contained"
						color="primary"
						startIcon={<Upload />}
						onClick={() => setShowUpload(true)}
					>
						Upload release
					</Button>
				</Grid2>
				<Grid2 size={{ xs: 12 }}>
					<Typography variant="body2" color="textSecondary">
						{activeRelease
							? `Active: ${activeRelease.Version} (build ${activeRelease.Build})${
									activeRelease.MinSupportedVersion
										? ` - devices below ${activeRelease.MinSupportedVersion} must update`
										: ""
								}`
							: "No active release: devices are not offered any update."}
					</Typography>
				</Grid2>
				<Grid2 size={{ xs: 12 }}>
					<Paper elevation={2} sx={{ height: "73vh", width: "100%" }}>
						<AgGridReact<AppRelease>
							loading={isLoading}
							rowData={releases ?? []}
							columnDefs={columnDefs}
							defaultColDef={defaultColDef}
							rowHeight={38}
							headerHeight={34}
							animateRows
							tooltipShowDelay={300}
							getRowId={(params) => String(params.data.AppReleaseId)}
							onRowDoubleClicked={(event: RowDoubleClickedEvent<AppRelease>) =>
								event.data && setReleaseToEdit(event.data)
							}
							onGridReady={(params: GridReadyEvent<AppRelease>) =>
								params.api.sizeColumnsToFit()
							}
							loadingOverlayComponent={CenteredLazyLoading}
							gridOptions={{
								theme: themeDXT,
								rowSelection: {
									mode: "singleRow" as const,
									checkboxes: false,
									enableClickSelection: true,
								},
							}}
						/>
					</Paper>
				</Grid2>
			</Grid2>
			<UploadReleaseModal
				open={showUpload}
				onHide={(uploaded) => {
					setShowUpload(false);
					if (uploaded) {
						setMessage("The release was uploaded successfully");
					}
				}}
				app={app}
				activeRelease={activeRelease}
			/>
			<EditReleaseModal
				release={releaseToEdit}
				onHide={(saved) => {
					setReleaseToEdit(null);
					if (saved) {
						setMessage("The release was updated successfully");
					}
				}}
			/>
			<ConfirmationDialogRaw
				open={releaseToToggle !== null}
				onClose={onToggleConfirmationClose}
				title={`${releaseToToggle?.IsActive ? "Deactivate" : "Activate"} Release`}
				suffixTitle="App Releases"
				message={
					releaseToToggle?.IsActive ? (
						<>
							You are about to deactivate version{" "}
							<b>
								{releaseToToggle?.Version} (build {releaseToToggle?.Build})
							</b>
							. Devices will not be offered any update until another release is
							activated. Continue?
						</>
					) : (
						<>
							You are about to activate version{" "}
							<b>
								{releaseToToggle?.Version} (build {releaseToToggle?.Build})
							</b>
							. It will replace the current active release and devices will be
							offered this update. Continue?
						</>
					)
				}
			/>
			<ConfirmationDialogRaw
				open={releaseToDelete !== null}
				onClose={onDeleteConfirmationClose}
				title="Delete Release"
				suffixTitle="App Releases"
				message={
					<>
						You are about to delete version{" "}
						<b>
							{releaseToDelete?.Version} (build {releaseToDelete?.Build})
						</b>
						. Are you sure you want to proceed?
					</>
				}
			/>
			<NotificationSnackBar message={message} onHide={() => setMessage("")} />
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error In App Releases"
			/>
		</>
	);
};
